import React, { useState, useRef, useEffect } from "react";
import DOLLUZ_CORP_thin_line_logo from "./assets/img/DOLLUZ_CORP_thin_line_logo.png";
import { apiFetch } from "./utils/api";
import { getVerifiedEmp, setVerifiedEmp } from "./utils/ticketSession";
import { FaTimes } from "react-icons/fa";
import MyTicketsList from "./MyTicketsList";
import { useNavigate } from "react-router-dom";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";
import "./Tickets.css";

const Tickets = () => {
    const [emailError, setEmailError] = useState("");
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false); // ✅ Loading state
    const [showPriority, setShowPriority] = useState(false);
    const [showContact, setShowContact] = useState(false);
    const priorityRef = useRef(null);
    const contactRef = useRef(null);
    const [otpCode, setOtpCode] = useState("");
    const [otpVerified, setOtpVerified] = useState(false);
    const [otpError, setOtpError] = useState("");
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [showOtpField, setShowOtpField] = useState(false);
    const [emailVerified, setEmailVerified] = useState(false);
    const [otpTimer, setOtpTimer] = useState(0);
    const otpTimerRef = useRef(null);

    // ✅ Custom div popup (replaces SweetAlert)
    const [popup, setPopup] = useState(null); // { icon, title, text }
    const showPopup = ({ icon, title, text }) => setPopup({ icon, title, text });
    const closePopup = () => setPopup(null);

    // ✅ Tabs: "create" (raise ticket) | "mytickets" (view raised tickets)
    const [activeTab, setActiveTab] = useState("create");
    // Create-form Category custom dropdown (design continuity with Priority)
    const [showCategoryCreate, setShowCategoryCreate] = useState(false);
    const categoryCreateRef = useRef(null);
    const categories = [
        { value: "IT", label: "IT" },
        { value: "HR", label: "HR" },
        { value: "Admin", label: "Admin" },
        { value: "Finance", label: "Finance" },
    ];

    useEffect(() => {
        // ✅ If the email was verified earlier in this session (e.g. "Raise
        // Another Ticket"), restore it and skip OTP. This in-memory value is
        // cleared on page reload/close, so a reload forces OTP again.
        const savedEmp = getVerifiedEmp();
        if (savedEmp) {
            restoreVerifiedEmp(savedEmp);
        } else {
            checkAutoLogin();
        }
        return () => {
            if (otpTimerRef.current) clearInterval(otpTimerRef.current);
        };
    }, []);

    // ✅ Restore a previously verified employee (same-session) without OTP
    const restoreVerifiedEmp = (emp) => {
        setFormData((prev) => ({
            ...prev,
            emp_id: emp.emp_id,
            email: emp.email,
            emp_first_name: emp.emp_first_name,
            emp_last_name: emp.emp_last_name,
            mobile: emp.mobile,
            altMobile: emp.altMobile,
            department: emp.department,
            employeeType: emp.employeeType,
            location: emp.location,
        }));
        setEmailVerified(true);
        setOtpVerified(true);
        setShowOtpField(false);
    };

    const checkAutoLogin = async () => {
        try {
            const res = await apiFetch("/api/tickets/check-auth", {
                method: "GET",
                credentials: "include"
            });

            const data = await res.json();

            if (data.loggedIn) {
                autoFillEmployee(data.emp);
            }
        } catch (err) {
            console.log("Auth check error", err);
        }
    };

    const autoFillEmployee = (emp) => {
        setFormData({
            ...formData,
            emp_id: emp.emp_id,
            email: emp.emp_mail_id,
            emp_first_name: emp.emp_first_name,
            emp_last_name: emp.emp_last_name,
            mobile: emp.emp_mobile_no,
            altMobile: emp.emp_alternate_mobile_no,
            department: emp.emp_department,
            employeeType: emp.emp_type,
            location: emp.emp_location,
        });

        setEmailVerified(true);
        setOtpVerified(true);
        setShowOtpField(false);
    };

    const { quill, quillRef } = useQuill({
        theme: "snow",
        modules: {
            toolbar: [
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ align: [] }],
                ["clean"],
            ],
        },
        formats: [
            "bold", "italic", "underline", "strike",
            "list", "bullet", "align",
        ],
    });

    // listen for changes
    useEffect(() => {
        if (quill) {
            quill.on("text-change", () => {
                handleDescriptionChange(quill.root.innerHTML);
            });
        }
    }, [quill]);

    const priorities = [
        {
            value: "High",
            label: "High",
            icon: (
                <span style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <i className="fa-solid fa-angle-up" style={{ color: "red", fontSize: "15px", lineHeight: "0.5" }}></i>
                    <i className="fa-solid fa-angle-up" style={{ color: "red", fontSize: "11px", lineHeight: "0.5" }}></i>
                    <i className="fa-solid fa-angle-up" style={{ color: "red", fontSize: "9px", lineHeight: "0.5" }}></i>
                </span>
            ),
        },
        {
            value: "Medium",
            label: "Medium",
            icon: (
                <span style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <i className="fa-solid fa-angle-up" style={{ color: "#FF8C00", fontSize: "14px", lineHeight: "0.6" }}></i>
                    <i className="fa-solid fa-angle-up" style={{ color: "#FF8C00", fontSize: "10px", lineHeight: "0.6" }}></i>
                </span>
            ),
        },
        {
            value: "Low",
            label: "Low",
            icon: (
                <span style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <i className="fa-solid fa-angle-up" style={{ color: "green", fontSize: "14px", lineHeight: "0.6" }}></i>
                </span>
            ),
        },
    ];

    const contactMethods = [
        {
            value: "Email",
            label: "Email",
            icon: <i className="fa-solid fa-envelope contact-icons email-icon"></i>,
        },
        {
            value: "Mobile",
            label: "Mobile/Phone",
            icon: <i className="fa-solid fa-mobile-screen contact-icons mobile-icon"></i>,
        },
        {
            value: "Cliq",
            label: "Cliq",
            icon: <i className="fa-brands fa-rocketchat contact-icons cliq-icon"></i>,
        },
    ];

    useEffect(() => {
        const handleOutsideClick = (e) => {
            // Close Priority dropdown if clicked outside
            if (priorityRef.current && !priorityRef.current.contains(e.target)) {
                setShowPriority(false);
            }

            // Close Contact dropdown if clicked outside
            if (contactRef.current && !contactRef.current.contains(e.target)) {
                setShowContact(false);
            }

            // Close Create-form Category dropdown if clicked outside
            if (categoryCreateRef.current && !categoryCreateRef.current.contains(e.target)) {
                setShowCategoryCreate(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    const [formData, setFormData] = useState({
        emp_first_name: "",
        emp_last_name: "",
        email: "",
        mobile: "",
        altMobile: "",
        department: "",
        employeeType: "",
        location: "",
        category: "",
        priority: "",
        contactMethod: "",
        subject: "",
        description: "",
        attachments: [],
    });

    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        if (!selectedFiles.length) return;

        // ✅ Max 3 files total
        if (formData.attachments.length + selectedFiles.length > 3) {
            showPopup({
                icon: "error",
                title: "Limit Exceeded",
                text: "You can upload a maximum of 3 files",
            });
            e.target.value = null;
            return;
        }

        // ✅ Check for duplicate file names
        const existingNames = formData.attachments.map(f => f.name);
        const duplicates = selectedFiles.filter(f => existingNames.includes(f.name));
        if (duplicates.length > 0) {
            showPopup({
                icon: "warning",
                title: "Duplicate File",
                text: `"${duplicates[0].name}" is already attached.`,
            });
            e.target.value = null;
            return;
        }

        // ✅ Total attachment size must not exceed 3 MB (all files combined,
        // any file type incl. zip). This is a per-field total, not per-file.
        const MAX_TOTAL_BYTES = 3 * 1024 * 1024;
        const existingSize = formData.attachments.reduce((sum, f) => sum + f.size, 0);
        const incomingSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
        if (existingSize + incomingSize > MAX_TOTAL_BYTES) {
            showPopup({
                icon: "error",
                title: "Size Limit Exceeded",
                text: "Total attachment size must not exceed 3 MB.",
            });
            e.target.value = null;
            return;
        }

        // Patterns to detect sensitive keys
        const secretPatterns = [
            /SG\.[A-Za-z0-9_-]+/g,        // SendGrid API Key
            /ghp_[A-Za-z0-9]+/g,          // GitHub Token
            /AIza[A-Za-z0-9-_]{35}/g      // Google API Key
        ];

        const validFiles = [];

        selectedFiles.forEach((file) => {

            const reader = new FileReader();

            reader.onload = (event) => {
                const content = event.target.result;

                for (let pattern of secretPatterns) {
                    if (pattern.test(content)) {
                        showPopup({
                            icon: "error",
                            title: "Invalid File",
                            text: `${file.name} contains sensitive information and cannot be uploaded.`,
                        });
                        return;
                    }
                }

                // ✅ File is safe → add to list
                validFiles.push(file);

                // ✅ When all files are processed
                if (validFiles.length === selectedFiles.length) {
                    setFormData((prev) => ({
                        ...prev,
                        attachments: [...prev.attachments, ...validFiles],
                    }));
                }
            };

            reader.onerror = () => {
                showPopup({
                    icon: "error",
                    title: "Read Error",
                    text: `Cannot read file ${file.name}`,
                });
            };

            reader.readAsText(file);
        });

        // Reset input so same file can be selected again
        e.target.value = null;
    };

    const removeFile = (index) => {
        setFormData((prev) => ({
            ...prev,
            attachments: prev.attachments.filter((_, i) => i !== index),
        }));
    };

    // ✅ Handle field changes
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "altMobile") {
            const numericValue = value.replace(/\D/g, "");
            setFormData((prev) => ({ ...prev, [name]: numericValue }));
            return;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (name === "email") {
            setEmailError("");
        }
    };

    const startOtpTimer = () => {
        if (otpTimerRef.current) clearInterval(otpTimerRef.current);
        setOtpTimer(120);
        otpTimerRef.current = setInterval(() => {
            setOtpTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(otpTimerRef.current);
                    otpTimerRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleResendOtp = async () => {
        setOtpCode("");
        setOtpError("");
        setIsSendingOtp(true);

        try {
            const res = await apiFetch("/api/tickets/send-otp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: formData.email }),
            });

            setIsSendingOtp(false);

            if (res.ok) {
                startOtpTimer();
                showPopup({
                    icon: "success",
                    title: "OTP Resent!",
                    text: "A new OTP has been sent to your email.",
                });
            } else {
                showPopup({
                    icon: "error",
                    title: "Failed to resend OTP",
                    text: "Please try again later.",
                });
            }
        } catch (err) {
            setIsSendingOtp(false);
            showPopup({
                icon: "error",
                title: "Error",
                text: "Failed to resend OTP. Please try again.",
            });
        }
    };

    const handleEmailSubmit = async () => {
        const value = formData.email;

        if (!value.includes("@")) {
            setEmailError("Invalid email format.");
            return;
        }

        try {
            const res = await apiFetch(`/api/tickets/employee/${value}`);

            // ❗ If backend returns 401 or 404, handle it gracefully
            if (!res.ok) {
                const errorData = await res.json().catch(() => null);

                if (res.status === 401 && errorData?.message) {
                    // 🔥 Show access denied error properly
                    setEmailError(errorData.message);
                    return;
                }

                throw new Error("Employee not found");
            }

            const data = await res.json();

            setFormData((prev) => ({
                ...prev,
                emp_id: data.emp_id,
                emp_first_name: data.emp_first_name,
                emp_last_name: data.emp_last_name,
                mobile: data.emp_mobile_no,
                department: data.emp_department,
                employeeType: data.emp_type,
                location: data.emp_location,
            }));

            // ✅ Email is now verified
            setEmailVerified(true);
            // ✅ Start Loader
            setIsSendingOtp(true);

            const otpResponse = await apiFetch("/api/tickets/send-otp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: value }),
            });

            // ✅ Stop Loader
            setIsSendingOtp(false);

            if (otpResponse.ok) {
                setShowOtpField(true);
                startOtpTimer();
                showPopup({
                    icon: "success",
                    title: "OTP Sent!",
                    text: "Please check your registered email for the OTP. It expires in 2 minutes.",
                });
            } else {
                showPopup({
                    icon: "error",
                    title: "Failed to send OTP",
                    text: "Please try again later.",
                });
            }

            setOtpVerified(false); // reset OTP verification
            setOtpCode("");
            setOtpError("");

        } catch (err) {
            setIsSendingOtp(false);
            setShowOtpField(false);
            setEmailError("This email is not registered with Dolluz Corp. Please contact admin (admin@dolluzcorp.com).");
            setFormData((prev) => ({ ...prev, emp_id: null, emp_first_name: "", emp_last_name: "" }));
        }
    };

    const handleOtpChange = (e) => {
        const value = e.target.value.replace(/\D/g, ""); // Only digits
        setOtpCode(value);

        if (value.length === 6) {
            verifyOTP(value);
        } else {
            setOtpError(""); // Clear error while typing
        }
    };

    const verifyOTP = async (code) => {
        setOtpError("");

        try {
            const res = await apiFetch("/api/tickets/verify-otp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: formData.email, otp: code }),
            });

            const data = await res.json();

            if (res.ok) {
                setOtpVerified(true);
                setOtpError("");
            } else {
                setOtpError(data.message || "Invalid OTP");
                setOtpVerified(false);
            }
        } catch (err) {
            setOtpError("Error verifying OTP. Please try again.");
            setOtpVerified(false);
        }
    };

    const handleDescriptionChange = (value) => {
        setFormData((prev) => ({
            ...prev,
            description: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const fieldNames = {
            email: "Email",
            emp_first_name: "First Name",
            emp_last_name: "Last Name",
            mobile: "Mobile No",
            altMobile: "Alternate Mobile No",
            department: "Department",
            employeeType: "Employee Type",
            location: "Location",
            category: "Category",
            priority: "Priority",
            contactMethod: "Preferred Contact Method",
            subject: "Subject",
            description: "Description",
        };

        // ✅ Validate fields in order
        const fieldOrder = [
            "email",
            "emp_first_name",
            "emp_last_name",
            "mobile",
            "altMobile",
            "department",
            "employeeType",
            "location",
            "category",
            "priority",
            "contactMethod",
            "subject",
            "description",
        ];

        const newErrors = {};
        for (let field of fieldOrder) {
            // Special validation for altMobile (optional field)
            if (field === "altMobile") {
                if (formData.altMobile && formData.altMobile.length !== 10) {
                    newErrors[field] = true;
                    showPopup({
                        icon: "error",
                        title: "Validation Error",
                        text: "Alternate Mobile No must be exactly 10 digits",
                    });
                    break;
                }
                continue;
            }

            // Special validation for subject length
            if (field === "subject" && formData.subject.length > 250) {
                newErrors[field] = true;
                showPopup({
                    icon: "error",
                    title: "Validation Error",
                    text: "Subject cannot exceed 250 characters",
                });
                break;
            }

            // General required field check
            if (!formData[field]) {
                newErrors[field] = true;
                showPopup({
                    icon: "error",
                    title: "Validation Error",
                    text: `${fieldNames[field]} is required`,
                });
                break;
            }
        }

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            setIsSubmitting(false);
            return;
        }

        // 🔹 ✅ Description length validation (min 300, max 5000)
        const plainTextDescription = quill ? quill.getText().trim() : "";
        if (
            plainTextDescription.length < 300 ||
            plainTextDescription.length > 5000) {
            showPopup({
                icon: "error",
                title: "Validation Error",
                text: "Description must be between 300 and 5000 characters",
            });
            setErrors(prev => ({ ...prev, description: true }));
            setIsSubmitting(false);
            return;
        }

        // ✅ Build FormData for file + text fields
        const formDataToSend = new FormData();

        // ✅ Append emp_id FIRST so multer can use it in the attachment filename
        if (formData.emp_id) {
            formDataToSend.append("emp_id", formData.emp_id);
        }

        for (const key in formData) {
            if (key === "emp_id") continue; // already appended above
            if (key === "attachments") {
                formData.attachments.forEach(file => {
                    formDataToSend.append("attachments", file);
                });
            } else if (formData[key]) {
                formDataToSend.append(key, formData[key]);
            }
        }

        // ✅ Append created_by as emp_id of logged-in employee
        if (formData.emp_id) {
            formDataToSend.append("created_by", formData.emp_id);
        } else {
            showPopup({
                icon: "warning",
                title: "Validation Error",
                text: "Please select a valid employee email before submitting the ticket."
            });
            setIsSubmitting(false);
            return;
        }

        try {
            const res = await apiFetch("/api/tickets/submit", {
                method: "POST",
                body: formDataToSend,
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.message || data.error || "Error submitting ticket");

            // ✅ Remember this verified employee for the session so "Raise
            // Another Ticket" skips OTP (cleared on page reload/close).
            setVerifiedEmp({
                emp_id: formData.emp_id,
                email: formData.email,
                emp_first_name: formData.emp_first_name,
                emp_last_name: formData.emp_last_name,
                mobile: formData.mobile,
                altMobile: formData.altMobile,
                department: formData.department,
                employeeType: formData.employeeType,
                location: formData.location,
            });

            // ✅ Redirect to Thank_You.js with data
            navigate("/thank-you", {
                state: { employeeName: formData.emp_first_name + " " + formData.emp_last_name, ticketId: data.ticket_id },
            });

            // ✅ Reset form
            setFormData({
                emp_first_name: "",
                emp_last_name: "",
                email: "",
                mobile: "",
                altMobile: "",
                department: "",
                employeeType: "",
                location: "",
                category: "",
                priority: "",
                contactMethod: "",
                subject: "",
                description: "",
                attachments: [],
                emp_id: null,
            });
            setErrors({});
            if (fileInputRef.current) {
                fileInputRef.current.value = null;
            }
        } catch (err) {
            showPopup({
                icon: "error",
                title: "Submission Error",
                text: err.message,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="tickets-container">
            <div className={`ticket-card ${activeTab === "mytickets" ? "wide" : ""}`}>
                <div className="ticket-logo">
                    <a className="navbar-brand" href="#">
                        <div className="ticket-logo">
                            <a className="navbar-brand" href="#">
                                <img
                                    src={DOLLUZ_CORP_thin_line_logo}
                                    alt="dassist logo_eagle"
                                />
                            </a>
                        </div>
                    </a>
                </div>

                <form className="ticket-form" onSubmit={handleSubmit}>
                    {/* Email */}
                    <label>Email {errors.email && <span style={{ color: "red" }}>* </span>} </label>
                    <div className="email-input-container">
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={emailVerified}  // ✅ Disable when verified
                            className={emailError ? "invalid" : ""}
                        />

                        {/* Show Arrow Icon Only When "@" Exists AND not yet verified */}
                        {formData.email.includes("@") && !emailVerified && !showOtpField && (
                            <i className="fa-solid fa-arrow-right email-action-icon" onClick={handleEmailSubmit}></i>
                        )}

                        {/* ✅ Show Success Icon When Email is Verified (not waiting for OTP) */}
                        {emailVerified && (
                            <i className="fa-solid fa-circle-check email-valid-icon"></i>
                        )}
                    </div>

                    {emailError && <p style={{ color: "red" }}>{emailError}</p>}

                    {/* Loader while sending OTP */}
                    {isSendingOtp && <div className="spinner"></div>}

                    {/* OTP Section */}
                    {showOtpField && !otpVerified && (
                        <div className="otp-section mt-2">
                            <label>Enter OTP</label>
                            <div className="otp-input-container">
                                <input
                                    type="text"
                                    value={otpCode}
                                    onChange={handleOtpChange}
                                    maxLength={6}
                                    disabled={otpTimer === 0}
                                    className={otpError ? "invalid" : otpCode.length === 6 && !otpError ? "valid" : ""}
                                />
                                {otpCode.length === 6 && !otpError && otpVerified && (
                                    <i className="fa-solid fa-circle-check email-valid-icon"></i>
                                )}
                            </div>
                            <div className="otp-timer-row">
                                {otpTimer > 0 ? (
                                    <span className="otp-timer">
                                        OTP expires in {Math.floor(otpTimer / 60)}:{String(otpTimer % 60).padStart(2, "0")}
                                    </span>
                                ) : (
                                    <span className="otp-expired">OTP expired</span>
                                )}
                                {otpTimer === 0 && (
                                    <button type="button" className="resend-otp-btn" onClick={handleResendOtp} disabled={isSendingOtp}>
                                        {isSendingOtp ? "Resending..." : "Resend OTP"}
                                    </button>
                                )}
                            </div>
                            {otpError && <p style={{ color: "red" }}>{otpError}</p>}
                        </div>
                    )}

                    {/* ✅ Create / My Tickets tabs — shown only after email verification */}
                    {formData.emp_id && otpVerified && (
                        <div className="ticket-tabs">
                            <button
                                type="button"
                                className={`ticket-tab ${activeTab === "create" ? "active" : ""}`}
                                onClick={() => setActiveTab("create")}
                            >
                                Create Ticket
                            </button>
                            <button
                                type="button"
                                className={`ticket-tab ${activeTab === "mytickets" ? "active" : ""}`}
                                onClick={() => setActiveTab("mytickets")}
                            >
                                Track My Tickets
                            </button>
                        </div>
                    )}

                    {/* Create panel — always mounted so Quill/state persist; toggled via display */}
                    <div
                        className={`ticket-fields tab-panel ${(!formData.emp_id || !otpVerified) ? "blurred" : ""}`}
                        style={{ display: activeTab === "create" ? "block" : "none" }}
                    >

                        {/* First Name & Last Name */}
                        <div className="ticket-row">
                            <div className="field">
                                <label>First Name {errors.emp_first_name && <span style={{ color: "red" }}>* </span>} </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.emp_first_name}
                                    onChange={handleChange}
                                    disabled
                                    className={errors.emp_first_name ? "invalid" : ""}
                                />
                            </div>
                            <div className="field">
                                <label>last Name {errors.emp_last_name && <span style={{ color: "red" }}>* </span>} </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.emp_last_name}
                                    onChange={handleChange}
                                    disabled
                                    className={errors.emp_last_name ? "invalid" : ""}
                                />
                            </div>
                        </div>

                        {/* Mobile & Alternate Mobile */}
                        <div className="ticket-row">
                            <div className="field">
                                <label>Mobile No {errors.mobile && <span style={{ color: "red" }}>* </span>} </label>
                                <input
                                    type="text"
                                    name="mobile"
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    disabled
                                    className={errors.mobile ? "invalid" : ""}
                                />
                            </div>
                            <div className="field">
                                <label>Alternate Mobile No</label>
                                <input
                                    type="text"
                                    name="altMobile"
                                    value={formData.altMobile}
                                    onChange={handleChange}
                                    maxLength={10}
                                    disabled={!formData.emp_id}
                                    className={errors.altMobile ? "invalid" : ""}
                                />
                            </div>
                        </div>

                        {/* Department & Employee Type */}
                        <div className="ticket-row">
                            <div className="field">
                                <label>Department {errors.department && <span style={{ color: "red" }}>* </span>} </label>
                                <input
                                    type="text"
                                    name="department"
                                    value={formData.department}
                                    onChange={handleChange}
                                    disabled
                                    className={errors.department ? "invalid" : ""}
                                />
                            </div>
                            <div className="field">
                                <label>Employee Type {errors.employeeType && <span style={{ color: "red" }}>* </span>} </label>
                                <input
                                    type="text"
                                    name="employeeType"
                                    value={formData.employeeType}
                                    onChange={handleChange}
                                    disabled
                                    className={errors.employeeType ? "invalid" : ""}
                                />
                            </div>
                        </div>

                        {/* Location & Category */}
                        <div className="ticket-row">
                            <div className="field">
                                <label>Location <span style={{ color: "red" }}>* </span> </label>
                                <input
                                    type="text"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleChange}
                                    disabled
                                    className={errors.location ? "invalid" : ""}
                                />
                            </div>
                            <div className="field">
                                <label>Category <span style={{ color: "red" }}>* </span> </label>

                                <div className={`priority-custom-dropdown ${errors.category ? "invalid" : ""}`} ref={categoryCreateRef}>
                                    <div
                                        className="selected-option"
                                        onClick={() => { if (formData.emp_id) setShowCategoryCreate(prev => !prev); }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            {formData.category
                                                ? categories.find(c => c.value === formData.category).label
                                                : "Select Category"}
                                        </div>
                                        <i className="fa-solid fa-angle-down priority-arrow"></i>
                                    </div>

                                    {showCategoryCreate && (
                                        <div className="priority-dropdown-menu">
                                            {categories.map((c) => (
                                                <div
                                                    key={c.value}
                                                    className="priority-dropdown-item"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleChange({ target: { name: "category", value: c.value } });
                                                        setShowCategoryCreate(false);
                                                    }}
                                                >
                                                    {c.label}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Priority & Preferred Contact Method */}
                        <div className="ticket-row">

                            {/* Priority Custom Dropdown */}
                            <div className="field">
                                <label>Priority <span style={{ color: "red" }}>* </span></label>

                                <div className="priority-custom-dropdown" ref={priorityRef}>
                                    <div className="selected-option" onClick={() => setShowPriority(prev => !prev)}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            {formData.priority
                                                ? priorities.find(p => p.value === formData.priority).icon
                                                : null}
                                            {formData.priority
                                                ? priorities.find(p => p.value === formData.priority).label
                                                : "Select Priority"}
                                        </div>
                                        <i className="fa-solid fa-angle-down priority-arrow"></i>
                                    </div>

                                    {/* Dropdown list */}
                                    {showPriority && (
                                        <div className="priority-dropdown-menu">
                                            {priorities.map((p) => (
                                                <div
                                                    key={p.value}
                                                    className="priority-dropdown-item"
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Prevent toggle
                                                        handleChange({ target: { name: "priority", value: p.value } });
                                                        setShowPriority(false);
                                                    }}
                                                >
                                                    {p.icon} {p.label}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="field">
                                <label>Preferred Contact Method <span style={{ color: "red" }}>* </span></label>

                                <div className="priority-custom-dropdown" ref={contactRef}>
                                    <div
                                        className="selected-option"
                                        onClick={() => setShowContact(prev => !prev)}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            {formData.contactMethod
                                                ? contactMethods.find(c => c.value === formData.contactMethod).icon
                                                : null}
                                            {formData.contactMethod
                                                ? contactMethods.find(c => c.value === formData.contactMethod).label
                                                : "Select Contact Method"}
                                        </div>
                                        <i className="fa-solid fa-angle-down priority-arrow"></i>
                                    </div>

                                    {showContact && (
                                        <div className="priority-dropdown-menu">
                                            {contactMethods.map(c => (
                                                <div
                                                    key={c.value}
                                                    className="priority-dropdown-item"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        handleChange({ target: { name: "contactMethod", value: c.value } });
                                                        setShowContact(false);
                                                    }}
                                                >
                                                    {c.icon} {c.label}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>

                        {/* Subject */}
                        <label>Subject <span style={{ color: "red" }}>* </span> </label>
                        <input
                            type="text"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            disabled={!formData.emp_id}
                            maxLength={250} // limit typing to 250
                            className={errors.subject ? "invalid" : ""}
                        />

                        {/* Description */}
                        <label>
                            Description <span style={{ color: "red" }}>*</span>
                        </label>

                        <div
                            ref={quillRef}
                            style={{ height: "150px", marginBottom: "20px" }}
                        />

                        {/* Attachment */}
                        <div className="attachment-field" style={{ position: "relative", display: "inline-block", width: "100%" }}>
                            <label>Attachment</label>
                            <input
                                type="file"
                                name="attachments"
                                ref={fileInputRef}
                                multiple
                                onChange={handleFileChange}
                                disabled={!formData.emp_id}
                            />

                            {formData.attachments.length > 0 && (
                                <div style={{ marginTop: "8px" }}>
                                    {formData.attachments.map((file, index) => (
                                        <div key={index} style={{ display: "flex", alignItems: "center" }}>
                                            <span>{file.name}</span>
                                            <FaTimes
                                                style={{ marginLeft: "10px", cursor: "pointer", color: "gray" }}
                                                onClick={() => removeFile(index)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Submit */}
                        <button type="submit" disabled={isSubmitting} className="submit-btn">
                            {isSubmitting ? "Submitting..." : "Submit"}
                        </button>

                        <p className="ticket-note">
                            <span className="note-title">Note:</span>

                            <div className="note-row">
                                <span className="note-index">1.</span>
                                <span className="note-content">
                                    Once submitted, your request will be routed to the respective support team.
                                    We are committed to addressing your concern as soon as possible.
                                    Kindly bear with us and we appreciate your patience.
                                </span>
                            </div>

                            <div className="note-row">
                                <span className="note-index">2.</span>
                                <span className="note-content">
                                    SLA: Low = &lt;48 hrs, Medium = &lt;24 hrs, High = &lt;8 hrs (Business Hours)
                                    <span className="note-icon-text">
                                        <i className="fa-solid fa-circle-exclamation"></i> Inappropriate priority will be deprioritized to max ETA.
                                    </span>
                                </span>
                            </div>
                        </p>
                    </div>

                    {/* ✅ My Tickets list — mounted once after verification, toggled via display */}
                    {otpVerified && (
                        <div
                            className="tab-panel"
                            style={{ display: activeTab === "mytickets" ? "block" : "none" }}
                        >
                            <MyTicketsList
                                empId={formData.emp_id}
                                showPopup={showPopup}
                                onCreateTicket={() => setActiveTab("create")}
                            />
                        </div>
                    )}
                </form>
            </div>

            {/* ✅ Custom div popup (replaces SweetAlert) */}
            {popup && (
                <div className="dpopup-overlay" onClick={closePopup}>
                    <div className="dpopup-card" onClick={(e) => e.stopPropagation()}>
                        <button className="dpopup-close" onClick={closePopup} aria-label="Close">
                            <FaTimes />
                        </button>
                        <div className={`dpopup-icon dpopup-icon-${popup.icon}`}>
                            {popup.icon === "success" && <i className="fa-solid fa-circle-check"></i>}
                            {popup.icon === "error" && <i className="fa-solid fa-circle-xmark"></i>}
                            {popup.icon === "warning" && <i className="fa-solid fa-triangle-exclamation"></i>}
                            {!["success", "error", "warning"].includes(popup.icon) && <i className="fa-solid fa-circle-info"></i>}
                        </div>
                        {popup.title && <div className="dpopup-title">{popup.title}</div>}
                        {popup.text && <div className="dpopup-text">{popup.text}</div>}
                        <button className="dpopup-ok" onClick={closePopup}>OK</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Tickets;
